<script setup lang="ts">
import { computed, ref } from "vue";
import { UiText, UiIcon, UiInfoBox, UiStandardButton, UiIconButton, UiDialog, UiCheckbox, UiTooltip, UiNavLink } from "@shopexperts/shared";
import type { AiSummaryKind, AiSummaryStep } from "@shopexperts/legacy-api";
import { useExpertAiSummaries } from "@/app/composables/expert-manager/useExpertAiSummaries";
import {
  AI_SUMMARY_KIND_STEP,
  AI_SUMMARY_STEP_LABELS,
  aiSummariesBlockedReason,
  overviewHasActiveRuns,
  overviewHasAnyRun,
  type AiSummariesBlockedReason,
} from "@/app/composables/expert-manager/aiSummaries";
import { hasAiContextValues } from "@/app/composables/expert-manager/aiContextForm";
import AiPublishedSummaryCard from "./AiPublishedSummaryCard.vue";
import AiPipelineSteps from "./AiPipelineSteps.vue";
import AiRunDetailDialog from "./AiRunDetailDialog.vue";
import AiRunHistoryDialog from "./AiRunHistoryDialog.vue";
import AiExpertContextForm from "./AiExpertContextForm.vue";

const props = defineProps<{
  expertId: number;
}>();

const {
  overview,
  overviewQuery,
  context,
  contextQuery,
  generate,
  regenerateStep,
  publish,
  updateContext,
} = useExpertAiSummaries(() => props.expertId);

const isLoading = computed(() => overviewQuery.isLoading.value);
const loadError = computed(() => overviewQuery.error.value);
const isRefreshing = computed(() => overviewQuery.isFetching.value && !overviewQuery.isLoading.value);
const hasActiveRuns = computed(() => overviewHasActiveRuns(overview.value));
const hasAnyRun = computed(() => overviewHasAnyRun(overview.value));
const isActing = computed(() => generate.isPending.value || regenerateStep.isPending.value || publish.isPending.value);

const blockedReason = ref<AiSummariesBlockedReason>(null);
const isBlocked = computed(() => blockedReason.value !== null);
const blockedTitle = computed(() =>
  blockedReason.value === "not_configured"
    ? "The AI provider isn't configured on this environment"
    : "AI summaries are switched off",
);
const blockedText = computed(() =>
  blockedReason.value === "not_configured"
    ? "OPENROUTER_API_KEY is missing on the API server, so nothing can be generated. Existing revisions stay readable."
    : "Turn on the master switch in the AI summaries settings to generate or regenerate. Existing revisions stay readable and nothing is shown on the public profile while it's off.",
);

function rememberBlock(error: unknown) {
  const reason = aiSummariesBlockedReason(error);
  if (reason) blockedReason.value = reason;
}

const generateOpen = ref(false);
async function confirmGenerate() {
  try {
    await generate.mutateAsync();
    blockedReason.value = null;
    generateOpen.value = false;
  } catch (error) {
    rememberBlock(error);
    generateOpen.value = false;
  }
}

const regenerateOpen = ref(false);
const regenerateTarget = ref<AiSummaryStep | null>(null);
const regenerateCascade = ref(true);
const regenerateDownstream = computed(() => {
  const step = regenerateTarget.value;
  if (!step || !overview.value) return [];
  const all = Object.keys(overview.value.steps) as AiSummaryStep[];
  const downstream = new Set<AiSummaryStep>();
  let frontier: AiSummaryStep[] = [step];
  while (frontier.length) {
    const next: AiSummaryStep[] = [];
    for (const candidate of all) {
      if (downstream.has(candidate) || candidate === step) continue;
      if (overview.value.steps[candidate].dependencies.some((dependency) => frontier.includes(dependency))) {
        downstream.add(candidate);
        next.push(candidate);
      }
    }
    frontier = next;
  }
  return [...downstream].map((entry) => AI_SUMMARY_STEP_LABELS[entry]);
});

function openRegenerate(step: AiSummaryStep) {
  regenerateTarget.value = step;
  regenerateCascade.value = true;
  regenerateOpen.value = true;
}

async function confirmRegenerate() {
  if (!regenerateTarget.value) return;
  try {
    await regenerateStep.mutateAsync({
      step: regenerateTarget.value,
      cascade: regenerateDownstream.value.length > 0 ? regenerateCascade.value : false,
    });
    blockedReason.value = null;
    regenerateOpen.value = false;
  } catch (error) {
    rememberBlock(error);
    regenerateOpen.value = false;
  }
}

const runDialogOpen = ref(false);
const runDialogId = ref<number | null>(null);
const runDialogCurrentId = computed(() => {
  if (!overview.value) return null;
  const { profile, reviews } = overview.value.summaries;
  if (profile && profile.run_id === runDialogId.value) return profile.run_id;
  if (reviews && reviews.run_id === runDialogId.value) return reviews.run_id;
  return historyCurrentRunId.value;
});

function openRun(runId: number) {
  runDialogId.value = runId;
  runDialogOpen.value = true;
}

const historyOpen = ref(false);
const historyStep = ref<AiSummaryStep | null>(null);
const historyCurrentRunId = computed(() => {
  if (!overview.value || !historyStep.value) return null;
  if (historyStep.value === "profile_summary") return overview.value.summaries.profile?.run_id ?? null;
  if (historyStep.value === "reviews_summary") return overview.value.summaries.reviews?.run_id ?? null;
  return null;
});

function openHistory(step: AiSummaryStep) {
  historyStep.value = step;
  historyOpen.value = true;
}

const publishConfirm = ref<{ kind: AiSummaryKind; runId: number } | null>(null);
async function confirmPublish() {
  if (!publishConfirm.value) return;
  try {
    await publish.mutateAsync(publishConfirm.value);
    publishConfirm.value = null;
    runDialogOpen.value = false;
  } catch {
    publishConfirm.value = null;
  }
}

async function saveContext(changes: Parameters<typeof updateContext.mutateAsync>[0]) {
  if (Object.keys(changes).length === 0) return;
  try {
    await updateContext.mutateAsync(changes);
  } catch {
    /* toast handled by the composable */
  }
}

const contextHasValues = computed(() => hasAiContextValues(context.value));
</script>

<template>
  <section class="flex flex-col gap-6">
    <header class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <UiIcon name="star-06" class="h-5 w-5 text-se-green" />
          <UiText as="h3" variant="h6" class="font-semibold text-se-dark" text="AI summaries" />
        </div>
        <UiText
          as="p"
          variant="p-small"
          class="max-w-2xl text-se-gray-600"
          text="Two AI-written summaries for the public profile, built by a five-step pipeline you can inspect step by step. Every run is kept as a revision, so regenerating never loses what was live before."
        />
      </div>
      <div class="flex shrink-0 items-center gap-2">
        <UiTooltip content="Global prompt, tone, and the master switch for every expert">
          <UiNavLink to="/admin/ai-summaries" class="inline-flex">
            <UiStandardButton variant="ghost" size="sm" icon="settings-01" label="Global settings" />
          </UiNavLink>
        </UiTooltip>
        <UiTooltip content="Reload the pipeline state">
          <UiIconButton
            icon="refresh-cw"
            aria-label="Refresh"
            variant="ghost"
            :disabled="isRefreshing"
            :class="isRefreshing ? '[&_svg]:animate-spin' : ''"
            @click="() => overviewQuery.refetch()"
          />
        </UiTooltip>
        <UiTooltip content="Run all five steps. Existing revisions are kept.">
          <UiStandardButton
            variant="primary"
            size="sm"
            icon="star-06"
            :label="hasAnyRun ? 'Regenerate everything' : 'Generate summaries'"
            :disabled="isActing || hasActiveRuns || isLoading || Boolean(loadError)"
            @click="generateOpen = true"
          />
        </UiTooltip>
      </div>
    </header>

    <UiInfoBox v-if="isBlocked" variant="warning" :title="blockedTitle">
      <div class="flex flex-col items-start gap-3">
        <UiText as="p" variant="p-small" class="text-se-gray-700" :text="blockedText" />
        <UiNavLink v-if="blockedReason === 'disabled'" to="/admin/ai-summaries" class="inline-flex">
          <UiStandardButton variant="outline" size="sm" label="Open AI summaries settings" />
        </UiNavLink>
      </div>
    </UiInfoBox>

    <UiInfoBox v-if="hasActiveRuns" variant="info" title="Pipeline running">
      <UiText
        as="p"
        variant="p-small"
        class="text-se-gray-700"
        text="Steps are queued on the worker and this page refreshes itself every few seconds. Large review sets can take several minutes on reasoning models."
      />
    </UiInfoBox>

    <UiInfoBox v-if="loadError" variant="error" title="Couldn't load AI summaries">
      <div class="flex flex-col items-start gap-3">
        <UiText as="p" variant="p-small" class="text-se-gray-700" text="Something went wrong loading the pipeline state for this expert." />
        <UiStandardButton variant="outline" size="sm" label="Retry" @click="() => overviewQuery.refetch()" />
      </div>
    </UiInfoBox>

    <div v-else-if="isLoading" class="grid gap-4 lg:grid-cols-2">
      <div v-for="n in 2" :key="n" class="h-48 animate-pulse rounded-xl bg-se-gray-100" />
      <div class="h-72 animate-pulse rounded-xl bg-se-gray-100 lg:col-span-2" />
    </div>

    <template v-else-if="overview">
      <div class="grid gap-4 lg:grid-cols-2">
        <AiPublishedSummaryCard
          kind="profile"
          :summary="overview.summaries.profile"
          :is-busy="isActing || hasActiveRuns"
          :can-regenerate="overview.steps.normalize_evidence.latest_run?.status === 'succeeded'"
          @regenerate="openRegenerate(AI_SUMMARY_KIND_STEP.profile)"
          @history="openHistory(AI_SUMMARY_KIND_STEP.profile)"
          @view-run="openRun"
        />
        <AiPublishedSummaryCard
          kind="reviews"
          :summary="overview.summaries.reviews"
          :is-busy="isActing || hasActiveRuns"
          :can-regenerate="overview.steps.normalize_evidence.latest_run?.status === 'succeeded'"
          @regenerate="openRegenerate(AI_SUMMARY_KIND_STEP.reviews)"
          @history="openHistory(AI_SUMMARY_KIND_STEP.reviews)"
          @view-run="openRun"
        />
      </div>

      <article class="flex flex-col gap-4 rounded-xl border border-se-gray-200 bg-se-white p-5">
        <div class="flex flex-col gap-1">
          <UiText as="h4" variant="h6" class="font-semibold text-se-dark" text="Pipeline" />
          <UiText
            as="p"
            variant="caption-normal"
            class="text-se-gray-500"
            text="Latest run per step, in the order they depend on each other. Regenerating a step reuses the newest usable output of the steps above it."
          />
        </div>
        <div
          v-if="!hasAnyRun"
          class="flex flex-col items-center gap-3 rounded-lg border border-dashed border-se-gray-300 px-6 py-10 text-center"
        >
          <UiIcon name="star-06" class="h-6 w-6 text-se-gray-300" />
          <UiText as="p" variant="p-small" class="font-semibold text-se-dark" text="Nothing has been generated for this expert yet" />
          <UiText
            as="p"
            variant="caption-normal"
            class="max-w-md text-se-gray-500"
            text="Generate summaries to run the pipeline. Add any admin context below first if you want it reflected in the first draft."
          />
        </div>
        <AiPipelineSteps
          v-else
          :overview="overview"
          :is-busy="isActing || hasActiveRuns"
          @regenerate="openRegenerate"
          @history="openHistory"
          @view-run="openRun"
        />
      </article>
    </template>

    <article class="flex flex-col gap-4 rounded-xl border border-se-gray-200 bg-se-white p-5">
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <UiText as="h4" variant="h6" class="font-semibold text-se-dark" text="Expert context" />
          <UiTooltip v-if="contextHasValues" content="This expert has admin-provided context that feeds every generation">
            <UiIcon name="check-circle-outline" class="h-4 w-4 text-se-green" />
          </UiTooltip>
        </div>
        <UiText
          as="p"
          variant="caption-normal"
          class="text-se-gray-500"
          text="Admin-only inputs for this expert, each with the trust level the model applies to it. They take effect on the next generation or regeneration."
        />
      </div>
      <div v-if="contextQuery.isLoading.value" class="grid gap-5 lg:grid-cols-2">
        <div v-for="n in 4" :key="n" class="h-28 animate-pulse rounded-lg bg-se-gray-100" />
      </div>
      <UiInfoBox v-else-if="contextQuery.error.value" variant="error" title="Couldn't load the expert context">
        <UiStandardButton variant="outline" size="sm" label="Retry" @click="() => contextQuery.refetch()" />
      </UiInfoBox>
      <AiExpertContextForm v-else :context="context" :is-saving="updateContext.isPending.value" @save="saveContext" />
    </article>

    <UiDialog
      v-if="generateOpen"
      v-model="generateOpen"
      :title="hasAnyRun ? 'Regenerate everything?' : 'Generate summaries?'"
      text="All five steps run in order: review themes and fact extraction first, then normalized evidence, then both summaries. Each step becomes a new revision; the summaries on the profile switch over only when their step succeeds."
      :primary-action-label="hasAnyRun ? 'Regenerate everything' : 'Generate'"
      secondary-action-label="Cancel"
      :loading="generate.isPending.value"
      @primary-action="confirmGenerate"
      @secondary-action="generateOpen = false"
    />

    <UiDialog
      v-if="regenerateOpen"
      v-model="regenerateOpen"
      :title="regenerateTarget ? `Regenerate ${AI_SUMMARY_STEP_LABELS[regenerateTarget].toLowerCase()}?` : 'Regenerate step?'"
      text="The step runs again from the newest usable outputs of the steps it depends on and is stored as a new revision."
      primary-action-label="Regenerate"
      secondary-action-label="Cancel"
      :loading="regenerateStep.isPending.value"
      @primary-action="confirmRegenerate"
      @secondary-action="regenerateOpen = false"
    >
      <UiCheckbox
        v-if="regenerateDownstream.length"
        v-model="regenerateCascade"
        label="Also re-run the steps that depend on it"
        :description="regenerateDownstream.join(', ')"
      />
      <UiText
        v-else
        as="p"
        variant="p-small"
        class="text-se-gray-600"
        text="No other step depends on this one, so only this step runs."
      />
    </UiDialog>

    <UiDialog
      v-if="publishConfirm"
      :model-value="true"
      title="Make this revision live?"
      text="The public profile switches to this revision immediately. The current one stays in the history and can be restored the same way."
      primary-action-label="Make live"
      secondary-action-label="Cancel"
      :loading="publish.isPending.value"
      @primary-action="confirmPublish"
      @secondary-action="publishConfirm = null"
      @close="publishConfirm = null"
    />

    <AiRunDetailDialog
      v-model="runDialogOpen"
      :expert-id="expertId"
      :run-id="runDialogId"
      :current-run-id="runDialogCurrentId"
      :is-publishing="publish.isPending.value"
      @publish="publishConfirm = $event"
    />

    <AiRunHistoryDialog
      v-model="historyOpen"
      :expert-id="expertId"
      :step="historyStep"
      :current-run-id="historyCurrentRunId"
      :is-publishing="publish.isPending.value"
      @view-run="openRun"
      @publish="publishConfirm = $event"
    />
  </section>
</template>
